import type { PickupDef } from '../data/types';
import { ARMOR_DUPLICATE_BONUS, BRIBE_DURATION_S, SIM_HZ, DOLLAR_POINTS, DOLLARS_PER_EXTRA_LIFE, MAX_LIVES, PICKUP_RADIUS } from '../data/tuning';
import { circleAabbOverlap, createCircle, type Aabb } from './collision';
import type { CoreWorld, TickContext } from './tick';

/**
 * Pickups are collected with a generous circle of PICKUP_RADIUS around the item, tested against the
 * player hitbox (GDD §7). Touching counts.
 */
export function isCollectable(player: Aabb, pickup: Pick<PickupDef, 'x' | 'y'>): boolean {
  return circleAabbOverlap(createCircle(pickup.x, pickup.y, PICKUP_RADIUS), player);
}

/**
 * One more life, never above MAX_LIVES (GDD §6.1). Emits EXTRA_LIFE only when a life is granted.
 * Also used by the Escapulario (#81).
 */
export function grantLife(world: CoreWorld, context: TickContext): boolean {
  if (world.lives >= MAX_LIVES) return false;
  world.lives += 1;
  context.emit({ type: 'EXTRA_LIFE', lives: world.lives });
  return true;
}

/**
 * One dollar bundle (GDD §6.1, §7, §12): DOLLAR_POINTS and one more dollar. Every hundred bundles the
 * counter goes back by 100 and a life is granted, so the counter always stays in 0..99 even when several
 * bundles are collected on the same tick. At 9 lives the counter still resets.
 */
export function collectDollar(world: CoreWorld, context: TickContext): void {
  world.pendingScore += DOLLAR_POINTS;
  world.dollars += 1;
  if (world.dollars >= DOLLARS_PER_EXTRA_LIFE) {
    world.dollars -= DOLLARS_PER_EXTRA_LIFE;
    grantLife(world, context);
  }
}

/**
 * An armor plate (GDD §7, §12, issue #79): it equips the armor, which absorbs one hit. Plates do not stack:
 * a second one while armored gives ARMOR_DUPLICATE_BONUS points instead. The first one gives no points.
 */
export function collectArmor(world: CoreWorld, context: TickContext): void {
  if (world.armor) {
    world.pendingScore += ARMOR_DUPLICATE_BONUS;
    context.emit({ type: 'powerup:armor-duplicate', points: ARMOR_DUPLICATE_BONUS });
    return;
  }
  world.armor = true;
  context.emit({ type: 'powerup:armor-collected' });
}

/** Protected ticks of a bribe (GDD §7): 6 s at 60 Hz. */
export const BRIBE_TICKS = Math.round(BRIBE_DURATION_S * SIM_HZ);

/**
 * A briefcase (GDD §7, issue #80): 6 s of invulnerability to enemies, obstacles and projectiles. The timer
 * counts down in phase 6 of this same tick, hence the extra tick: it reads 360 right after the pickup and
 * still protects in phase 5 of the 360th tick after it. A second briefcase resets the timer (no stacking,
 * no points; @gdd-pending).
 */
export function collectBribe(world: CoreWorld, context: TickContext): void {
  const wasActive = world.bribeRemaining > 0;
  world.bribeRemaining = BRIBE_TICKS + 1;
  if (!wasActive) context.emit({ type: 'powerup:bribe-started' });
}

/**
 * The scapular (GDD §7, issue #81): one more life, up to MAX_LIVES. At the maximum it is still collected
 * (it disappears) but adds nothing. The life is saved with the next event of GDD §6.4, not on its own.
 */
export function collectScapular(world: CoreWorld, context: TickContext): void {
  grantLife(world, context);
  context.emit({ type: 'pickup:scapular', lives: world.lives });
}
